<script setup lang="ts">
import { cardParticles, hexAlpha, mixColor, xpPairLabel } from '@/utils/levels';

// Faixa de nível do jogo no DESKTOP (Figma 22582:64412). Mesmo conteúdo da
// versão mobile — [emblema] · [Elo: lv N] · [barra] · [NN%] —, mas NÃO é a
// mesma faixa com breakpoints: aqui é uma PLACA solta, com moldura e fundo
// próprios, e nenhuma medida bate com a do mobile (texto 20px contra 11,2px,
// trilha de 26px contra 10px, emblema de 65px contra 19px). Por isso mora num
// arquivo separado, e não num `lg:` do LevelsInGameProgress.
//
// O fundo é TINGIDO pelo elo: no Figma o gradiente vai de #3D146D a #080923, e
// #3D146D é exatamente o violeta da Ametista afogado em 66% do #080923 — então
// a mesma conta serve pra qualquer elo, sem uma tabela paralela de fundos.
//
// AQUI a faixa É clicável e leva pra página de níveis — ao contrário da versão
// mobile, que é só leitura de propósito: lá ela mora a um dedo do topo do jogo e
// um toque errado tirava o jogador da partida. No desktop o alvo é o cursor, o
// clique é deliberado, e a placa fica acima do frame — então ela é um link.
const {
  tier,
  level,
  xp,
  levelXp,
  levelXpSpan,
  isMaxLevel,
  levelProgress,
  currentLevelLabel,
} = useLevelsProgress();

// Os mesmos benefícios do mobile, das `core_public_tags`. Lista vazia (perfil
// ainda não chegou, ou jogador sem nada ligado) simplesmente não rende chip
// nenhum — a linha continua existindo pro contador de XP.
const { activeBenefits } = useLevelBenefits();

const progressLabel = computed(() => Math.round(levelProgress.value));

// Mesmo contador do mobile: o XP dentro do nível atual, mesma regra de
// encurtar. Aqui ele nem chega perto de faltar espaço, mas duas formatações pro
// mesmo número seriam duas verdades.
const xpLabel = computed(() =>
  isMaxLevel.value
    ? xpPairLabel(xp.value)
    : xpPairLabel(levelXp.value, levelXpSpan.value),
);

// As mesmas partículas da pele dos elos (LevelsTierSkin / cartão do jogador):
// aqui elas entram SOLTAS, sem a pele inteira, porque o desenho desta faixa pede
// a moldura chapada e o fundo próprio dele — o que se aproveita é só a poeira
// pulsando atrás do conteúdo.
const particles = computed(() => cardParticles(tier.value));

const CARD_BASE = '#080923';

// A silhueta é a MESMA do mobile — faixa larga em cima, degrau curto embaixo,
// chanfro fechando —, só que aqui quem a veste é o próprio card: o gradiente do
// elo e a poeira são o fundo, e o que sobra ao lado do degrau fica VAZADO, com o
// fundo da página aparecendo por baixo do contador de XP.
//
// As três peças (faixa, degrau e chanfro) pintam o MESMO gradiente de 73px de
// altura, cada uma deslocada da sua altura — é isso que faz a cor atravessar a
// emenda sem degrau de tom. Se alguma dessas medidas mudar, as três mudam
// juntas, e é por isso que elas moram aqui e não espalhadas no CSS.
const CARD_H = 73;
const BAND_H = 44;

const cardGradient = computed(
  () =>
    `linear-gradient(180deg,${mixColor(tier.value.color, CARD_BASE, 0.66)},${CARD_BASE})`,
);

/** Fundo de uma peça: o gradiente do card inteiro, puxado pra cima em `offset`. */
function pieceBackground(offset: number) {
  return {
    backgroundImage: cardGradient.value,
    backgroundSize: `100% ${CARD_H}px`,
    backgroundPosition: `0 -${offset}px`,
  };
}

// O chanfro é SVG, então o gradiente dele é declarado em coordenadas do próprio
// `viewBox` (1 unidade = 1,5px, e o topo do degrau é o y=0 de lá). Estas duas
// pontas são o y=0 e o y=73 do card traduzidos pra essa régua.
const CHAMFER_SCALE = 1.5;
const chamferGradientY1 = (0 - BAND_H) / CHAMFER_SCALE;
const chamferGradientY2 = (CARD_H - BAND_H) / CHAMFER_SCALE;

// A MOLDURA de 1px em gradiente saiu junto com o retângulo: um `border` só sabe
// contornar a caixa, e a silhueta agora tem dois degraus e uma diagonal. Refazer
// o traço nessa forma exigiria desenhar card e contorno como um SVG único que se
// remede a cada mudança no número de chips — e sem ele o gradiente do elo já
// define a forma contra o fundo da página.
</script>

<template>
  <NuxtLink
    :aria-label="`${currentLevelLabel} — ${$t('casino.game.levels')}`"
    class="levels-ingame-desktop relative block h-[73px] w-full select-none hover:brightness-125"
    :draggable="false"
    no-prefetch
    style="--chip-scale: 1.5"
    :to="localeLink('/vip/levels')"
  >
    <!-- FAIXA DE CIMA: atravessa o card inteiro e carrega a trilha. Quina de
         baixo à esquerda RETA, porque é de lá que o degrau continua — se
         arredondasse abriria um entalhe no meio do card. -->
    <div
      class="deskBand"
      :style="pieceBackground(0)"
    >
      <!-- Poeira do elo. `overflow: clip` e não `hidden`: `hidden` cria contexto
           de rolagem, e o card mora a um dedo do frame do jogo. -->
      <div
        aria-hidden="true"
        class="deskDust"
      >
        <span
          v-for="(particle, index) in particles"
          :key="index"
          class="levels-ingame-pop absolute rounded-full"
          :style="{
            left: particle.left,
            top: particle.top,
            width: particle.size,
            height: particle.size,
            background: particle.color,
            boxShadow: `0 0 ${particle.blur} ${particle.color}`,
            animationDuration: particle.duration,
            animationDelay: particle.delay,
          }"
        ></span>
      </div>

      <div class="deskBand__bar">
        <!-- Trilha: `flex items-center` em vez de preenchimento absoluto, porque
             o desenho centra uma barra de 24,89px dentro dos 26px da calha — com
             o filho posicionado no fluxo os 0,55px de folga saem de graça. -->
        <span
          class="levels-ingame-track flex h-[26px] min-w-0 flex-1 items-center overflow-hidden rounded-full shadow-[inset_0_1.973px_3.946px_rgba(0,0,0,0.5)]"
        >
          <span
            class="block h-[24.89px] rounded-full"
            :style="{
              width: `${levelProgress}%`,
              background: `linear-gradient(90deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.42)})`,
              boxShadow: `0 0 15.782px ${hexAlpha(tier.color, 0.9)}`,
            }"
          ></span>
        </span>

        <span
          class="shrink-0 text-[20px] font-bold leading-none text-white tabular-nums"
        >
          {{ progressLabel }}%
        </span>
      </div>
    </div>

    <!-- DEGRAU: começa na borda esquerda do card (é ele que pinta o fundo
         debaixo do emblema) e termina onde os chips terminam. O que fica à
         direita dele NÃO tem fundo nenhum — é a página aparecendo, e é sobre ela
         que o contador de XP se apoia. -->
    <div class="deskStepRow">
      <div
        class="deskStep"
        :style="pieceBackground(BAND_H)"
      >
        <div
          aria-hidden="true"
          class="deskDust deskDust--step"
        >
          <span
            v-for="(particle, index) in particles"
            :key="index"
            class="levels-ingame-pop absolute rounded-full"
            :style="{
              left: particle.left,
              top: particle.top,
              width: particle.size,
              height: particle.size,
              background: particle.color,
              boxShadow: `0 0 ${particle.blur} ${particle.color}`,
              animationDuration: particle.duration,
              animationDelay: particle.delay,
            }"
          ></span>
        </div>

        <LevelsBenefitChip
          v-for="benefit in activeBenefits"
          :key="benefit.id"
          :benefit="benefit"
        />

        <span
          v-if="!activeBenefits.length"
          class="deskXp deskXp--inside"
          >{{ xpLabel }}</span
        >

        <!-- Chanfro: o path do "Union" do mobile, e o gradiente dele declarado
             em coordenadas do próprio viewBox pra continuar o das outras duas
             peças em vez de recomeçar. -->
        <svg
          aria-hidden="true"
          class="deskChamfer"
          viewBox="0 -1 22.78 20.5"
        >
          <defs>
            <linearGradient
              id="deskChamferFill"
              gradientUnits="userSpaceOnUse"
              x1="0"
              x2="0"
              :y1="chamferGradientY1"
              :y2="chamferGradientY2"
            >
              <stop
                offset="0"
                :stop-color="mixColor(tier.color, CARD_BASE, 0.66)"
              />
              <stop
                offset="1"
                :stop-color="CARD_BASE"
              />
            </linearGradient>
          </defs>

          <path
            d="M0-1H22.78V0C20.56 0 18.46 1 17.07 2.72L5.71 16.77C4.32 18.5 2.22 19.5 0 19.5Z"
            fill="url(#deskChamferFill)"
          />
        </svg>
      </div>

      <span
        v-if="activeBenefits.length"
        class="deskXp deskXp--outside"
        >{{ xpLabel }}</span
      >
    </div>

    <!-- Bloco do elo POR CIMA das duas peças, e não dentro de uma delas: o
         emblema tem 65px e é centrado no card inteiro, enquanto a faixa de cima
         tem 44 — dentro dela o brasão vazaria pra fora do card. -->
    <div class="deskElo">
      <!-- Bloco do elo: caixa de 228px com o emblema e o rótulo nas pontas
           (`justify-between` do desenho). `shrink-0` porque quem cede largura é
           a trilha. -->
      <div class="flex w-[228px] shrink-0 items-center justify-between">
        <!-- Emblema RECORTADO, não encaixado: a arte é escalada a 80,83px
             (108,3% da largura da janela, como no desenho) e a janela de
             74,638 × 65,208 corta o que passa. Com `bg-contain` o brasão saía
             visivelmente menor que o do Figma, porque a arte é quadrada e a
             janela é mais baixa que larga.
             O recorte é CENTRADO, e não puxado 8,19px pra cima como o Figma
             pede: aquele valor foi desenhado sobre a Ametista, cuja arte é um
             losango; nas 7 faixas VIP, que são coroas assentadas mais embaixo
             na tela quadrada, ele decepava a base da coroa. Centrado, os 15,6px
             de sobra saem metade de cada lado e as duas famílias de arte
             sobrevivem. -->
        <span
          aria-hidden="true"
          class="h-[65.208px] w-[74.638px] flex-none bg-center bg-no-repeat [overflow:clip]"
          :style="{
            backgroundImage: `url(${tier.badge})`,
            backgroundSize: '80.83px 80.83px',
            filter: 'drop-shadow(0 2.426px 8.491px rgba(0,0,0,.55))',
          }"
        ></span>

        <!-- `whitespace-nowrap`: os 150px do desenho foram medidos com
             "Ametista: lv 20", e "Diamante: lv 26" pede 147 — com o arredondamento
             da fonte isso estourava e o rótulo quebrava em duas linhas nos níveis
             26 a 28. O que sobra invade o vão de 15px até a trilha, que é folga
             morta, em vez de virar segunda linha. -->
        <span
          class="w-[150px] whitespace-nowrap text-[20px] font-semibold leading-none text-white/60"
        >
          <b class="font-bold text-white">{{ tier.name }}:</b>
          lv {{ level }}
        </span>
      </div>
    </div>
  </NuxtLink>
</template>

<style scoped lang="postcss">
/* As três medidas que amarram tudo: a faixa de cima, o degrau, e a coluna da
   esquerda (emblema + rótulo + vão) que os dois têm que vencer antes de chegar
   na trilha e nos chips. 44 + 29 = 73, a altura do card. */
.levels-ingame-desktop {
  --desk-band-h: 44px;
  --desk-step-h: 29px;
  --desk-left-col: 249.5px;

  -webkit-user-drag: none;
}

/* O bloco do elo flutua por cima das duas peças de fundo, ocupando o card
   inteiro — é o que deixa o emblema de 65px centrado nos 73 do card em vez de
   nos 44 da faixa. `pointer-events-none` porque quem recebe o clique é o link
   inteiro; sem isso o emblema viraria um buraco no meio do alvo. */
.deskElo {
  @apply pointer-events-none absolute inset-y-0 left-0 flex items-center;

  padding-left: 6.5px;
}

.deskBand {
  @apply relative;

  height: var(--desk-band-h);
  /* Sem raio embaixo à esquerda: é de lá que o degrau continua. */
  border-radius: 12px 12px 12px 0;
}

.deskBand__bar {
  @apply relative flex h-full items-center;

  padding: 0 8.53px 0 var(--desk-left-col);
  gap: 15px;
}

/* Poeira: uma camada por peça, cada uma recortada pela sua própria forma. É o
   preço de o fundo ter virado duas caixas em vez de uma — e sai barato, porque
   a lista de partículas é a mesma e só o recorte muda. */
.deskDust {
  @apply pointer-events-none absolute inset-0;

  border-radius: inherit;
  overflow: clip;
}

.deskDust--step {
  /* No degrau elas ficam escondidas atrás dos chips na maior parte do tempo; o
     que se vê é o brilho passando na sobra à esquerda. */
  border-radius: 0 0 0 12px;
}

.deskStepRow {
  @apply flex items-start;
}

/* Degrau: mede o conteúdo, mas nasce colado na borda esquerda do card, porque é
   ele quem pinta o fundo debaixo do emblema. */
.deskStep {
  @apply relative flex w-fit shrink-0 items-center;

  height: var(--desk-step-h);
  padding: 0 4.53px 0 var(--desk-left-col);
  gap: 6.03px;
  border-radius: 0 0 0 12px;
}

/* O chanfro sobe 1,5px (o `-1` do viewBox nesta escala) por cima da faixa, pra
   um arredondamento de subpixel não abrir um fio entre as duas peças. */
.deskChamfer {
  @apply absolute left-full block;

  top: -1.5px;
  width: 34.17px;
  height: calc(var(--desk-step-h) + 1.5px);
}

.deskXp {
  @apply flex shrink-0 items-center italic tabular-nums;

  height: var(--desk-step-h);
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

/* Fora da chapa ele come o que sobra do degrau e cola na direita, como no
   mobile. `relative` porque o chanfro avança ~34px pra dentro deste espaço. */
.deskXp--outside {
  @apply relative min-w-0 flex-1 justify-end overflow-hidden;

  padding-right: 8.53px;
}

/* Dentro da chapa (sem benefício nenhum) ele é o conteúdo do degrau: branco
   chapado, como no mobile. */
.deskXp--inside {
  color: #fff;
}
/* Clicar e arrastar na placa não seleciona nada nem sai carregando fantasma.
   Quem faz o trabalho aqui é o `draggable="false"` do template: um <a> é
   arrastável por padrão, e sem ele o Chromium disparava um `dragstart` e o
   cursor saía com o fantasma do atalho (medido: 1 evento antes, 0 depois).
   O `-webkit-user-drag` cobre o WebKit, que ignora o atributo em alguns casos.

   O `select-none` do template é cinto e suspensório: no Chromium o texto dentro
   de um link já não seleciona por arraste nem por duplo-clique (conferido), mas
   Firefox e Safari selecionam, e a proteção sobrevive se a placa algum dia
   deixar de ser um link.

   O emblema não precisa de tratamento próprio — é `background-image`, e fundo
   não se arrasta; se um dia virar <img>, aí sim ele vira alvo de arraste. */

/* Calha vazia: o Figma pede o azul-marinho chapado `rgba(0,25,101,.74)`, mas
   correndo no sentido da barra ela ganha profundidade — escura onde o XP começa,
   clara onde ele vai chegar. A ponta direita para em 68% de opacidade de
   propósito: mais clara que isso e o vazio começa a competir com o cheio nos
   elos claros (Diamante, Prata). O azul NÃO é tingido pelo elo — é ele que
   sustenta o contraste com a barra colorida. */
.levels-ingame-track {
  background: linear-gradient(
    90deg,
    rgba(0, 10, 42, 0.86),
    rgba(0, 44, 138, 0.68)
  );
}

/* O reset global mata `transition`, então a pulsação anda por keyframes. Mesma
   curva da pele dos elos (LevelsTierSkin), pra a poeira não pulsar de dois
   jeitos diferentes na mesma sessão. */
@keyframes levelsIngamePop {
  0%,
  100% {
    opacity: 0;
    transform: translateY(4px) scale(0.6);
  }
  45% {
    opacity: 0.9;
    transform: translateY(-4px) scale(1);
  }
}

.levels-ingame-pop {
  opacity: 0;
  animation-name: levelsIngamePop;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
</style>
